import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, ShoppingCart, Wallet, Users, Boxes, AlertTriangle, Factory,
  Plus, Truck, Receipt, DollarSign,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";
import api, { taka } from "@/lib/api";
import { StatCard } from "@/pages/admin/AdminUI";

const INV_LABEL = { paddy: "ধান", rice: "চাল", broken: "খুদ", bran: "ব্রান", husk: "তুষ", powder: "গুঁড়া", other: "অন্যান্য" };

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [modules, setModules] = useState([]);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/auth/permissions").then((r) => setModules(r.data.modules)).catch(() => {});
  }, []);
  if (!d) return <div className="text-stone-500">লোড হচ্ছে...</div>;

  const invData = Object.entries(d.inventory).map(([k, v]) => ({ name: INV_LABEL[k] || k, qty: v }));
  const allow = (m) => modules.includes("*") || modules.includes(m);

  const quick = [
    { to: "/admin/orders", icon: ShoppingCart, label: "নতুন অর্ডার", mod: "orders" },
    { to: "/admin/sales", icon: Receipt, label: "নতুন বিক্রয়", mod: "sales" },
    { to: "/admin/purchases", icon: Truck, label: "ধান ক্রয়", mod: "purchases" },
    { to: "/admin/production", icon: Factory, label: "প্রোডাকশন", mod: "production" },
    { to: "/admin/stock", icon: Boxes, label: "স্টক যোগ", mod: "stock" },
    { to: "/admin/expenses", icon: Wallet, label: "খরচ যোগ", mod: "expenses" },
    { to: "/admin/customers", icon: Users, label: "কাস্টমার", mod: "customers" },
    { to: "/admin/suppliers", icon: UserCogIcon, label: "সরবরাহকারী", mod: "purchases" },
  ].filter((q) => allow(q.mod));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#1E3A2B]">ড্যাশবোর্ড</h1>
        <p className="text-stone-500 text-sm">ব্যবসার সারাংশ ও পরিসংখ্যান</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={TrendingUp} label="আজকের বিক্রি" value={taka(d.today_sales)} color="emerald" />
        <StatCard icon={ShoppingCart} label="আজকের অর্ডার" value={d.today_orders} color="blue" />
        <StatCard icon={DollarSign} label="মোট বিক্রি" value={taka(d.total_sales)} color="purple" />
        <StatCard icon={Wallet} label="নিট লাভ" value={taka(d.net_profit)} color="amber" />
        <StatCard icon={ShoppingCart} label="পেন্ডিং অর্ডার" value={d.pending_orders} color="amber" />
        <StatCard icon={ShoppingCart} label="সম্পন্ন অর্ডার" value={d.completed_orders} color="emerald" />
        <StatCard icon={Users} label="মোট কাস্টমার" value={d.total_customers} color="blue" />
        <StatCard icon={Boxes} label="স্টক মূল্য" value={taka(d.stock_value)} color="stone" />
      </div>

      <div>
        <h2 className="font-bold text-[#1E3A2B] mb-3">দ্রুত কাজ</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-3">
          {quick.map((q) => (
            <Link key={q.label} to={q.to} data-testid={`quick-${q.label}`} className="bg-white rounded-xl border border-stone-200 p-3 flex flex-col items-center gap-2 text-center hover:border-[#CA8A04] hover:shadow-sm transition-all">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><q.icon size={20} /></div>
              <span className="text-xs font-medium text-stone-700">{q.label}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-stone-200 p-5">
          <h3 className="font-bold text-[#1E3A2B] mb-4">গত ৭ দিনের বিক্রি</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={d.sales_trend}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1E3A2B" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#1E3A2B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip formatter={(v) => taka(v)} />
              <Area type="monotone" dataKey="sales" stroke="#1E3A2B" strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 p-5">
          <h3 className="font-bold text-[#1E3A2B] mb-4 flex items-center gap-2"><Factory size={18} /> স্টক (কেজি)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={invData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="name" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Bar dataKey="qty" fill="#CA8A04" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-stone-200 p-5">
          <h3 className="font-bold text-[#1E3A2B] mb-3">আজকের উৎপাদন</h3>
          <div className="text-3xl font-bold text-emerald-800">{d.today_production_rice} <span className="text-base font-normal text-stone-500">কেজি চাল</span></div>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 p-5">
          <h3 className="font-bold text-[#1E3A2B] mb-3 flex items-center gap-2"><AlertTriangle size={18} className="text-amber-500" /> কম স্টক সতর্কতা</h3>
          {d.low_stock.length === 0 ? <div className="text-sm text-stone-400">সব ঠিক আছে ✅</div> : (
            <div className="space-y-2">
              {d.low_stock.map((p) => (
                <div key={p.id} className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2" data-testid={`low-stock-${p.id}`}>
                  <span className="text-sm font-medium text-amber-900">⚠️ {p.name_bn}</span>
                  <span className="text-sm text-amber-700">{Math.round(p.stock)} / {p.min_stock} {p.unit}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UserCogIcon(props) { return <Users {...props} />; }
